import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { smoothScrollTo } from "@/lib/SmoothScroll";

const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Products", id: "products" },
  { label: "Process", id: "process" },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) smoothScrollTo(el, { offset: -80 });
    } else {
      navigate(`/#${id}`);
    }
  };

  const goToInquiry = () => goTo("inquiry");

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "backdrop-blur-xl bg-white/85 border-b border-black/5"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center"
          onClick={() => isHome && smoothScrollTo(0)}
          data-testid="nav-logo-button"
          aria-label="Rahilaxmi Exports — Home"
        >
          <Logo variant="light" size="md" />
        </Link>

        <nav
          className="hidden lg:flex items-center gap-9"
          aria-label="Primary"
        >
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => goTo(link.id)}
              className="group relative text-[13px] font-medium text-[#0A192F] uppercase tracking-[0.14em] py-2"
              data-testid={`nav-link-${link.id}`}
            >
              <span className="relative z-10">{link.label}</span>
              <span className="absolute left-0 -bottom-0.5 h-[1px] w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-500" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            onClick={goToInquiry}
            className="group relative inline-flex items-center gap-2 px-6 py-3 bg-[#0A192F] text-white text-[11px] font-semibold tracking-[0.18em] uppercase overflow-hidden"
            data-testid="nav-cta-quote-button"
          >
            <span className="absolute inset-0 bg-[#D4AF37] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            <span className="relative z-10 group-hover:text-[#0A192F] transition-colors duration-500">
              Get a Quote
            </span>
            <ArrowUpRight
              size={14}
              className="relative z-10 group-hover:text-[#0A192F] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 duration-500"
            />
          </button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-[#0A192F]"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden bg-white border-t border-black/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => goTo(link.id)}
                  className="text-left text-base font-medium text-[#0A192F] py-2"
                  data-testid={`mobile-nav-link-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={goToInquiry}
                className="mt-3 px-6 py-3 bg-[#0A192F] text-white font-semibold tracking-[0.18em] uppercase text-xs"
                data-testid="mobile-nav-cta-quote"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
